import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileText, Search, Truck, Eye, Printer, Upload, X, Image, CreditCard, Receipt, FileImage } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoadSlipsPage = () => {
  const [loadSlips, setLoadSlips] = useState([]);
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [selectedSlip, setSelectedSlip] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [uploading, setUploading] = useState({ pan: false, rc: false, signature: false });

  const [formData, setFormData] = useState({
    slip_number: "",
    date: new Date().toISOString().split('T')[0],
    client_id: "",
    client_name: "",
    vendor_id: "",
    vendor_name: "",
    driver_id: "",
    driver_name: "",
    driver_mobile: "",
    driver_license: "",
    vehicle_number: "",
    vehicle_type: "",
    vehicle_capacity: "",
    origin: "",
    destination: "",
    material_type: "",
    quantity: "",
    unit: "tons",
    freight_amount: "",
    advance_amount: "0",
    diesel_amount: "0",
    cash_amount: "0",
    owner_name: "",
    pan_image_url: "",
    rc_image_url: "",
    cheque_image_url: "",
    authorized_signature_url: "",
    // Bank account details
    bank_account_id: "",
    bank_account_name: "",
    bank_name: "",
    bank_account_number: "",
    bank_ifsc_code: "",
    bank_branch: "",
    remarks: "",
    status: "pending"
  });
  
  const [selectedVendorDrivers, setSelectedVendorDrivers] = useState([]);
  const [selectedVendorVehicles, setSelectedVendorVehicles] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(null);
  const signatureInputRef = useRef(null);
  const panInputRef = useRef(null);
  const rcInputRef = useRef(null);
  const chequeInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [slipsRes, clientsRes, vendorsRes, employeesRes, bankAccountsRes] = await Promise.all([
        axios.get(`${API_URL}/load-slips`),
        axios.get(`${API_URL}/clients`),
        axios.get(`${API_URL}/vendors`),
        axios.get(`${API_URL}/employees`),
        axios.get(`${API_URL}/bank-accounts`)
      ]);
      setLoadSlips(slipsRes.data);
      setClients(clientsRes.data);
      setVendors(vendorsRes.data);
      setEmployees(employeesRes.data.filter(e => e.designation === "Driver"));
      setBankAccounts(bankAccountsRes.data.filter(acc => acc.is_active));
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch truck details when vehicle number is entered
  const fetchTruckDetails = async (vehicleNumber) => {
    if (!vehicleNumber || vehicleNumber.length < 4) return;
    
    try {
      const response = await axios.get(`${API_URL}/trucks/by-number/${encodeURIComponent(vehicleNumber)}`);
      const data = response.data;
      
      if (data.found) {
        // Auto-populate fields from truck master
        setFormData(prev => ({
          ...prev,
          owner_name: data.owner_name || prev.owner_name,
          pan_image_url: data.pan_image_url || prev.pan_image_url,
          rc_image_url: data.rc_image_url || prev.rc_image_url,
          driver_id: data.driver_id || prev.driver_id,
          driver_name: data.driver_name || prev.driver_name,
          truck_id: data.truck_id
        }));
        
        if (data.pan_image_url || data.rc_image_url) {
          toast.success("Vehicle documents auto-loaded from Truck Master");
        }
      }
    } catch (error) {
      // Silently fail - vehicle might not be in truck master
      console.log("Vehicle not found in truck master");
    }
  };

  // Debounced vehicle number change handler
  const handleVehicleNumberChange = (value) => {
    setFormData(prev => ({ ...prev, vehicle_number: value }));
    
    // Debounce the API call
    if (window.vehicleSearchTimeout) {
      clearTimeout(window.vehicleSearchTimeout);
    }
    window.vehicleSearchTimeout = setTimeout(() => {
      fetchTruckDetails(value);
    }, 500);
  };

  // Handle vendor selection - load vendor's drivers and vehicles
  const handleVendorChange = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    if (vendor) {
      setFormData(prev => ({
        ...prev,
        vendor_id: vendorId,
        vendor_name: vendor.name,
        owner_name: vendor.name
      }));
      setSelectedVendorDrivers(vendor.drivers || []);
      setSelectedVendorVehicles(vendor.vehicles || []);
    }
  };

  // Handle driver selection from vendor's drivers
  const handleVendorDriverChange = (driverIndex) => {
    if (driverIndex === "manual") {
      setFormData(prev => ({
        ...prev,
        driver_id: "",
        driver_name: "",
        driver_mobile: "",
        driver_license: ""
      }));
    } else {
      const driver = selectedVendorDrivers[parseInt(driverIndex)];
      if (driver) {
        setFormData(prev => ({
          ...prev,
          driver_id: `vendor-driver-${driverIndex}`,
          driver_name: driver.name || "",
          driver_mobile: driver.mobile || "",
          driver_license: driver.license_number || ""
        }));
      }
    }
  };

  // Handle vehicle selection from vendor's vehicles
  const handleVendorVehicleChange = (vehicleIndex) => {
    if (vehicleIndex === "manual") {
      setFormData(prev => ({
        ...prev,
        vehicle_number: "",
        vehicle_type: "",
        vehicle_capacity: ""
      }));
    } else {
      const vehicle = selectedVendorVehicles[parseInt(vehicleIndex)];
      if (vehicle) {
        setFormData(prev => ({
          ...prev,
          vehicle_number: vehicle.vehicle_number || "",
          vehicle_type: vehicle.vehicle_type || "",
          vehicle_capacity: vehicle.capacity || ""
        }));
        // Also fetch from truck master if exists
        fetchTruckDetails(vehicle.vehicle_number);
      }
    }
  };

  // Handle signature upload
  const handleSignatureUpload = async (file) => {
    if (!file) return;
    
    setUploading(prev => ({ ...prev, signature: true }));
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${API_URL}/upload-image`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData(prev => ({
        ...prev,
        authorized_signature_url: response.data.url
      }));
      toast.success("Signature uploaded successfully");
    } catch (error) {
      console.error("Signature upload error:", error);
      toast.error("Failed to upload signature");
    } finally {
      setUploading(prev => ({ ...prev, signature: false }));
    }
  };

  // Generic document image upload handler
  const handleDocumentUpload = async (file, fieldName, docType) => {
    if (!file) return;
    
    setUploadingImage(fieldName);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${API_URL}/upload-image`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData(prev => ({
        ...prev,
        [fieldName]: response.data.url
      }));
      toast.success(`${docType} uploaded successfully`);
    } catch (error) {
      console.error(`${docType} upload error:`, error);
      toast.error(`Failed to upload ${docType}`);
    } finally {
      setUploadingImage(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        quantity: parseFloat(formData.quantity),
        freight_amount: parseFloat(formData.freight_amount),
        advance_amount: parseFloat(formData.advance_amount || 0),
        diesel_amount: parseFloat(formData.diesel_amount || 0),
        cash_amount: parseFloat(formData.cash_amount || 0)
      };

      if (selectedSlip) {
        await axios.put(`${API_URL}/load-slips/${selectedSlip.id}`, submitData);
        toast.success("Load slip updated successfully");
      } else {
        await axios.post(`${API_URL}/load-slips`, submitData);
        toast.success("Load slip created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleEdit = (slip) => {
    setSelectedSlip(slip);
    // Load vendor's drivers and vehicles if vendor is selected
    if (slip.vendor_id) {
      const vendor = vendors.find(v => v.id === slip.vendor_id);
      if (vendor) {
        setSelectedVendorDrivers(vendor.drivers || []);
        setSelectedVendorVehicles(vendor.vehicles || []);
      }
    }
    setFormData({
      slip_number: slip.slip_number,
      date: slip.date,
      client_id: slip.client_id,
      client_name: slip.client_name,
      vendor_id: slip.vendor_id || "",
      vendor_name: slip.vendor_name || "",
      driver_id: slip.driver_id || "",
      driver_name: slip.driver_name || "",
      driver_mobile: slip.driver_mobile || "",
      driver_license: slip.driver_license || "",
      vehicle_number: slip.vehicle_number,
      vehicle_type: slip.vehicle_type || "",
      vehicle_capacity: slip.vehicle_capacity || "",
      origin: slip.origin,
      destination: slip.destination,
      material_type: slip.material_type,
      quantity: slip.quantity.toString(),
      unit: slip.unit,
      freight_amount: slip.freight_amount.toString(),
      advance_amount: slip.advance_amount?.toString() || "0",
      diesel_amount: slip.diesel_amount?.toString() || "0",
      cash_amount: slip.cash_amount?.toString() || "0",
      owner_name: slip.owner_name || "",
      pan_image_url: slip.pan_image_url || "",
      rc_image_url: slip.rc_image_url || "",
      cheque_image_url: slip.cheque_image_url || "",
      authorized_signature_url: slip.authorized_signature_url || "",
      // Bank account details
      bank_account_id: slip.bank_account_id || "",
      bank_account_name: slip.bank_account_name || "",
      bank_name: slip.bank_name || "",
      bank_account_number: slip.bank_account_number || "",
      bank_ifsc_code: slip.bank_ifsc_code || "",
      bank_branch: slip.bank_branch || "",
      remarks: slip.remarks || "",
      status: slip.status
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/load-slips/${selectedSlip.id}`);
      toast.success("Load slip deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedSlip(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete load slip");
    }
  };

  const handlePreview = async (slip) => {
    try {
      const response = await axios.get(`${API_URL}/load-slips/${slip.id}/html`);
      setPreviewHtml(response.data);
      setSelectedSlip(slip);
      setPreviewDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load preview");
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(previewHtml);
    printWindow.document.close();
    printWindow.print();
  };

  const resetForm = () => {
    setSelectedSlip(null);
    setSelectedVendorDrivers([]);
    setSelectedVendorVehicles([]);
    setFormData({
      slip_number: "",
      date: new Date().toISOString().split('T')[0],
      client_id: "",
      client_name: "",
      vendor_id: "",
      vendor_name: "",
      driver_id: "",
      driver_name: "",
      driver_mobile: "",
      driver_license: "",
      vehicle_number: "",
      vehicle_type: "",
      vehicle_capacity: "",
      origin: "",
      destination: "",
      material_type: "",
      quantity: "",
      unit: "tons",
      freight_amount: "",
      advance_amount: "0",
      diesel_amount: "0",
      cash_amount: "0",
      owner_name: "",
      pan_image_url: "",
      rc_image_url: "",
      cheque_image_url: "",
      authorized_signature_url: "",
      // Bank account details reset
      bank_account_id: "",
      bank_account_name: "",
      bank_name: "",
      bank_account_number: "",
      bank_ifsc_code: "",
      bank_branch: "",
      remarks: "",
      status: "pending"
    });
  };

  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setFormData({
      ...formData,
      client_id: clientId,
      client_name: client?.company_name || ""
    });
  };

  const handleDriverChange = (driverId) => {
    const driver = employees.find(e => e.id === driverId);
    setFormData({
      ...formData,
      driver_id: driverId,
      driver_name: driver?.name || ""
    });
  };

  // Calculate balance when freight or advance changes
  const calculateBalance = () => {
    const freight = parseFloat(formData.freight_amount) || 0;
    const advance = parseFloat(formData.advance_amount) || 0;
    return freight - advance;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "border-yellow-500/50 text-yellow-400 bg-yellow-500/10",
      in_transit: "border-blue-500/50 text-blue-400 bg-blue-500/10",
      delivered: "border-green-500/50 text-green-400 bg-green-500/10",
      cancelled: "border-red-500/50 text-red-400 bg-red-500/10"
    };
    return styles[status] || styles.pending;
  };

  const filteredSlips = loadSlips.filter(slip =>
    slip.slip_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    slip.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    slip.origin.toLowerCase().includes(searchTerm.toLowerCase()) ||
    slip.destination.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (slip.owner_name && slip.owner_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-primary animate-pulse font-heading text-xl">Loading load slips...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="load-slips-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-3">
            <FileText className="w-8 h-8 text-primary" />
            Load Slip / Broker Slip
          </h1>
          <p className="text-muted-foreground mt-1">Create and manage transport load slips with payment details</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="glow-primary bg-primary hover:bg-primary/90"
          data-testid="add-load-slip-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Load Slip
        </Button>
      </div>

      {/* Search */}
      <Card className="border-border bg-card">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by slip number, client, owner, origin, or destination..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-secondary/50 border-border"
              data-testid="load-slip-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-border bg-card">
        <CardHeader className="border-b border-border/50">
          <CardTitle className="font-heading">All Load Slips ({filteredSlips.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Slip No.</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Date</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Client</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Owner</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Route</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Freight</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Balance</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Status</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSlips.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      No load slips found. Create your first load slip to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSlips.map((slip) => (
                    <TableRow key={slip.id} className="border-border table-row-hover" data-testid={`load-slip-row-${slip.id}`}>
                      <TableCell className="font-mono text-primary">{slip.slip_number}</TableCell>
                      <TableCell className="text-muted-foreground">{slip.date}</TableCell>
                      <TableCell className="font-medium text-foreground">{slip.client_name}</TableCell>
                      <TableCell className="text-muted-foreground">{slip.owner_name || slip.vendor_name || '-'}</TableCell>
                      <TableCell className="text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <span>{slip.origin}</span>
                          <Truck className="w-3 h-3" />
                          <span>{slip.destination}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-foreground">{formatCurrency(slip.freight_amount)}</TableCell>
                      <TableCell className="font-mono text-orange-400">{formatCurrency(slip.balance_amount)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={getStatusBadge(slip.status)}>
                          {slip.status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handlePreview(slip)}
                            data-testid={`preview-load-slip-${slip.id}`}
                            title="Preview & Print"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(slip)}
                            data-testid={`edit-load-slip-${slip.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => { setSelectedSlip(slip); setDeleteDialogOpen(true); }}
                            data-testid={`delete-load-slip-${slip.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {selectedSlip ? "Edit Load Slip" : "Create New Load Slip"}
            </DialogTitle>
            <DialogDescription>
              {selectedSlip ? "Update load slip details" : "Enter the details for the new load slip / broker slip"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="bg-secondary/50 border-border"
                  required
                  data-testid="load-slip-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client">Client</Label>
                <Select value={formData.client_id} onValueChange={handleClientChange}>
                  <SelectTrigger className="bg-secondary/50 border-border" data-testid="load-slip-client-select">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>{client.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="vendor">Vendor (Optional)</Label>
                <Select value={formData.vendor_id} onValueChange={handleVendorChange}>
                  <SelectTrigger className="bg-secondary/50 border-border" data-testid="load-slip-vendor-select">
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map((vendor) => (
                      <SelectItem key={vendor.id} value={vendor.id}>{vendor.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="owner_name">Owner Name</Label>
                <Input
                  id="owner_name"
                  value={formData.owner_name}
                  onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                  className="bg-secondary/50 border-border"
                  placeholder="Vehicle/Broker Owner Name"
                  data-testid="load-slip-owner-input"
                />
              </div>
            </div>

            {/* Driver Details Section */}
            <div className="p-3 bg-secondary/30 rounded-lg space-y-3">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <Truck className="w-4 h-4" /> Driver & Vehicle Details
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Select Driver</Label>
                  {selectedVendorDrivers.length > 0 ? (
                    <Select onValueChange={handleVendorDriverChange}>
                      <SelectTrigger className="bg-secondary/50 border-border">
                        <SelectValue placeholder="Select from vendor's drivers" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">Enter Manually</SelectItem>
                        {selectedVendorDrivers.map((driver, index) => (
                          <SelectItem key={index} value={index.toString()}>
                            {driver.name} - {driver.mobile}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Select value={formData.driver_id} onValueChange={handleDriverChange}>
                      <SelectTrigger className="bg-secondary/50 border-border" data-testid="load-slip-driver-select">
                        <SelectValue placeholder="Select driver" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map((driver) => (
                          <SelectItem key={driver.id} value={driver.id}>{driver.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Select Vehicle</Label>
                  {selectedVendorVehicles.length > 0 ? (
                    <Select onValueChange={handleVendorVehicleChange}>
                      <SelectTrigger className="bg-secondary/50 border-border">
                        <SelectValue placeholder="Select from vendor's vehicles" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">Enter Manually</SelectItem>
                        {selectedVendorVehicles.map((vehicle, index) => (
                          <SelectItem key={index} value={index.toString()}>
                            {vehicle.vehicle_number} - {vehicle.vehicle_type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : null}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Driver Name</Label>
                  <Input
                    value={formData.driver_name}
                    onChange={(e) => setFormData({ ...formData, driver_name: e.target.value })}
                    className="bg-secondary/50 border-border h-8 text-sm"
                    placeholder="Driver name"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Driver Mobile</Label>
                  <Input
                    value={formData.driver_mobile}
                    onChange={(e) => setFormData({ ...formData, driver_mobile: e.target.value })}
                    className="bg-secondary/50 border-border h-8 text-sm"
                    placeholder="9876543210"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">License Number</Label>
                  <Input
                    value={formData.driver_license}
                    onChange={(e) => setFormData({ ...formData, driver_license: e.target.value })}
                    className="bg-secondary/50 border-border h-8 text-sm"
                    placeholder="DL-1234567890"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Vehicle Number *</Label>
                  <Input
                    value={formData.vehicle_number}
                    onChange={(e) => handleVehicleNumberChange(e.target.value)}
                    className="bg-secondary/50 border-border h-8 text-sm"
                    placeholder="MH-12-AB-1234"
                    required
                    data-testid="load-slip-vehicle-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Vehicle Type</Label>
                  <Input
                    value={formData.vehicle_type}
                    onChange={(e) => setFormData({ ...formData, vehicle_type: e.target.value })}
                    className="bg-secondary/50 border-border h-8 text-sm"
                    placeholder="Truck/Trailer"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Capacity</Label>
                  <Input
                    value={formData.vehicle_capacity}
                    onChange={(e) => setFormData({ ...formData, vehicle_capacity: e.target.value })}
                    className="bg-secondary/50 border-border h-8 text-sm"
                    placeholder="20 tons"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="origin">Origin</Label>
                <Input
                  id="origin"
                  value={formData.origin}
                  onChange={(e) => setFormData({ ...formData, origin: e.target.value })}
                  className="bg-secondary/50 border-border"
                  placeholder="Mumbai"
                  required
                  data-testid="load-slip-origin-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="destination">Destination</Label>
                <Input
                  id="destination"
                  value={formData.destination}
                  onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                  className="bg-secondary/50 border-border"
                  placeholder="Delhi"
                  required
                  data-testid="load-slip-destination-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="material_type">Material Type</Label>
                <Input
                  id="material_type"
                  value={formData.material_type}
                  onChange={(e) => setFormData({ ...formData, material_type: e.target.value })}
                  className="bg-secondary/50 border-border"
                  placeholder="Steel Coils"
                  required
                  data-testid="load-slip-material-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="quantity">Quantity</Label>
                <Input
                  id="quantity"
                  type="number"
                  step="0.01"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  className="bg-secondary/50 border-border"
                  placeholder="10"
                  required
                  data-testid="load-slip-quantity-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="unit">Unit</Label>
                <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                  <SelectTrigger className="bg-secondary/50 border-border" data-testid="load-slip-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tons">Tons</SelectItem>
                    <SelectItem value="kg">Kg</SelectItem>
                    <SelectItem value="pieces">Pieces</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Payment Details Section */}
            <div className="border border-border rounded-lg p-4 space-y-4 bg-secondary/20">
              <h3 className="font-semibold text-primary">Payment Details</h3>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="freight_amount">Total Freight (₹)</Label>
                  <Input
                    id="freight_amount"
                    type="number"
                    value={formData.freight_amount}
                    onChange={(e) => setFormData({ ...formData, freight_amount: e.target.value })}
                    className="bg-secondary/50 border-border"
                    placeholder="50000"
                    required
                    data-testid="load-slip-freight-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="advance_amount">Advance Amount (₹)</Label>
                  <Input
                    id="advance_amount"
                    type="number"
                    value={formData.advance_amount}
                    onChange={(e) => setFormData({ ...formData, advance_amount: e.target.value })}
                    className="bg-secondary/50 border-border"
                    placeholder="10000"
                    data-testid="load-slip-advance-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Balance Amount (₹)</Label>
                  <div className="h-10 px-3 py-2 bg-secondary/30 border border-border rounded-md flex items-center font-mono text-orange-400">
                    {formatCurrency(calculateBalance())}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="diesel_amount">Diesel Amount (₹)</Label>
                  <Input
                    id="diesel_amount"
                    type="number"
                    value={formData.diesel_amount}
                    onChange={(e) => setFormData({ ...formData, diesel_amount: e.target.value })}
                    className="bg-secondary/50 border-border"
                    placeholder="5000"
                    data-testid="load-slip-diesel-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cash_amount">Cash Amount (₹)</Label>
                  <Input
                    id="cash_amount"
                    type="number"
                    value={formData.cash_amount}
                    onChange={(e) => setFormData({ ...formData, cash_amount: e.target.value })}
                    className="bg-secondary/50 border-border"
                    placeholder="5000"
                    data-testid="load-slip-cash-input"
                  />
                </div>
              </div>
            </div>

            {/* Document Images Upload Section */}
            <div className="p-4 bg-secondary/30 border border-border rounded-lg space-y-4">
              <Label className="text-sm font-semibold flex items-center gap-2">
                <FileImage className="w-4 h-4" />
                Vehicle & Payment Documents
              </Label>
              <p className="text-xs text-muted-foreground">Upload PAN Card, Vehicle RC, and Cheque images for records</p>
              
              <div className="grid grid-cols-3 gap-4">
                {/* PAN Card Upload */}
                <div className="space-y-2">
                  <Label className="text-xs">PAN Card</Label>
                  <input
                    type="file"
                    accept="image/*"
                    ref={panInputRef}
                    className="hidden"
                    onChange={(e) => handleDocumentUpload(e.target.files[0], 'pan_image_url', 'PAN Card')}
                  />
                  {formData.pan_image_url ? (
                    <div className="relative border border-border rounded-lg p-2 bg-background">
                    <img 
                      src={formData.pan_image_url} 
                      alt="PAN Card" 
                      className="h-20 w-full object-cover rounded"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute -top-2 -right-2 h-5 w-5"
                      onClick={() => setFormData({ ...formData, pan_image_url: "" })}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-20 border-dashed flex flex-col gap-1"
                    onClick={() => panInputRef.current?.click()}
                    disabled={uploadingImage === 'pan_image_url'}
                    data-testid="upload-pan-btn"
                  >
                    {uploadingImage === 'pan_image_url' ? (
                      <span className="animate-pulse text-xs">Uploading...</span>
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5" />
                        <span className="text-xs">PAN Card</span>
                      </>
                    )}
                  </Button>
                )}
              </div>
              
              {/* RC Upload */}
              <div className="space-y-2">
                <Label className="text-xs">Vehicle RC</Label>
                <input
                  type="file"
                  accept="image/*"
                  ref={rcInputRef}
                  className="hidden"
                  onChange={(e) => handleDocumentUpload(e.target.files[0], 'rc_image_url', 'Vehicle RC')}
                />
                {formData.rc_image_url ? (
                  <div className="relative border border-border rounded-lg p-2 bg-background">
                    <img 
                      src={formData.rc_image_url} 
                      alt="Vehicle RC" 
                      className="h-20 w-full object-cover rounded"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute -top-2 -right-2 h-5 w-5"
                      onClick={() => setFormData({ ...formData, rc_image_url: "" })}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-20 border-dashed flex flex-col gap-1"
                    onClick={() => rcInputRef.current?.click()}
                    disabled={uploadingImage === 'rc_image_url'}
                    data-testid="upload-rc-btn"
                  >
                    {uploadingImage === 'rc_image_url' ? (
                      <span className="animate-pulse text-xs">Uploading...</span>
                    ) : (
                      <>
                        <FileText className="w-5 h-5" />
                        <span className="text-xs">Vehicle RC</span>
                      </>
                    )}
                  </Button>
                )}
              </div>
              
              {/* Cheque Upload */}
              <div className="space-y-2">
                <Label className="text-xs">Cheque Copy</Label>
                <input
                  type="file"
                  accept="image/*"
                  ref={chequeInputRef}
                  className="hidden"
                  onChange={(e) => handleDocumentUpload(e.target.files[0], 'cheque_image_url', 'Cheque')}
                />
                {formData.cheque_image_url ? (
                  <div className="relative border border-border rounded-lg p-2 bg-background">
                    <img 
                      src={formData.cheque_image_url} 
                      alt="Cheque" 
                      className="h-20 w-full object-cover rounded"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute -top-2 -right-2 h-5 w-5"
                      onClick={() => setFormData({ ...formData, cheque_image_url: "" })}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-20 border-dashed flex flex-col gap-1"
                    onClick={() => chequeInputRef.current?.click()}
                    disabled={uploadingImage === 'cheque_image_url'}
                    data-testid="upload-cheque-btn"
                  >
                    {uploadingImage === 'cheque_image_url' ? (
                      <span className="animate-pulse text-xs">Uploading...</span>
                    ) : (
                      <>
                        <Receipt className="w-5 h-5" />
                        <span className="text-xs">Cheque Copy</span>
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
              
          {/* Authorized Signature Upload */}
          <div className="pt-3 border-t border-border">
            <Label className="mb-2 block">Authorized Signature</Label>
            <input
              type="file"
              accept="image/*"
              ref={signatureInputRef}
              className="hidden"
              onChange={(e) => handleSignatureUpload(e.target.files[0])}
            />
            {formData.authorized_signature_url ? (
              <div className="relative border border-border rounded-lg p-2 bg-secondary/30 inline-block">
                <img 
                  src={formData.authorized_signature_url} 
                  alt="Authorized Signature" 
                  className="h-16 object-contain"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute -top-2 -right-2 h-5 w-5"
                  onClick={() => setFormData({ ...formData, authorized_signature_url: "" })}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="h-16 px-8 border-dashed"
                onClick={() => signatureInputRef.current?.click()}
                disabled={uploading.signature}
                data-testid="upload-signature-btn"
              >
                {uploading.signature ? (
                  <span className="animate-pulse">Uploading...</span>
                ) : (
                  <div className="flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    <span>Upload Signature</span>
                  </div>
                )}
              </Button>
            )}
          </div>

          {selectedSlip && (
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger className="bg-secondary/50 border-border" data-testid="load-slip-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_transit">In Transit</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Bank Account for Payment */}
            <div className="space-y-2 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <Label className="text-blue-400 font-semibold flex items-center gap-2">
                🏦 Payment Bank Account (for Client)
              </Label>
              <p className="text-xs text-muted-foreground mb-2">
                Select a bank account to display on the load slip for payment collection
              </p>
              <Select 
                value={formData.bank_account_id || "none"} 
                onValueChange={(v) => {
                  if (v === "none") {
                    setFormData({ 
                      ...formData, 
                      bank_account_id: "",
                      bank_account_name: "",
                      bank_name: "",
                      bank_account_number: "",
                      bank_ifsc_code: "",
                      bank_branch: ""
                    });
                  } else {
                    const selectedAccount = bankAccounts.find(acc => acc.id === v);
                    if (selectedAccount) {
                      setFormData({ 
                        ...formData, 
                        bank_account_id: v,
                        bank_account_name: selectedAccount.account_name,
                        bank_name: selectedAccount.bank_name,
                        bank_account_number: selectedAccount.account_number,
                        bank_ifsc_code: selectedAccount.ifsc_code,
                        bank_branch: selectedAccount.branch || ""
                      });
                    }
                  }
                }}
              >
                <SelectTrigger className="bg-secondary/50 border-border" data-testid="load-slip-bank-account-select">
                  <SelectValue placeholder="Select bank account for payment..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- No Bank Account --</SelectItem>
                  {bankAccounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.account_name} - {account.bank_name} ({account.account_number?.slice(-4)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.bank_account_id && (
                <div className="mt-2 p-3 bg-secondary/30 rounded text-sm space-y-1">
                  <p><span className="text-muted-foreground">Account:</span> <span className="font-medium">{formData.bank_account_name}</span></p>
                  <p><span className="text-muted-foreground">Bank:</span> <span className="font-medium">{formData.bank_name}</span></p>
                  <p><span className="text-muted-foreground">A/C No:</span> <span className="font-mono font-medium">{formData.bank_account_number}</span></p>
                  <p><span className="text-muted-foreground">IFSC:</span> <span className="font-mono font-medium">{formData.bank_ifsc_code}</span></p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="remarks">Remarks (Optional)</Label>
              <Input
                id="remarks"
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                className="bg-secondary/50 border-border"
                placeholder="Any additional notes..."
                data-testid="load-slip-remarks-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="glow-primary bg-primary" data-testid="load-slip-submit-btn">
                {selectedSlip ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Delete Load Slip</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete load slip "{selectedSlip?.slip_number}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-load-slip">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="bg-white text-black max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-black flex items-center justify-between">
              <span>Load Slip Preview - {selectedSlip?.slip_number}</span>
              <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-load-slip">
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div 
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoadSlipsPage;
